import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeClosed, RefreshDouble, WarningCircle } from 'iconoir-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetProfile,
  useUpdateProfile,
  useChangePassword,
} from '../../features/settings/hooks';

type FormValues = {
  full_name: string;
  email: string;
  telephone?: string | null;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
};

function AppManagedField({
  label,
  id,
  registration,
  error,
  type = 'text',
  placeholder,
  managedHint,
}: {
  label: string;
  id: string;
  registration: React.InputHTMLAttributes<HTMLInputElement>;
  error?: string;
  type?: string;
  placeholder?: string;
  managedHint: string;
}) {
  return (
    <div className="flex flex-col gap-[4px] w-full">
      <label htmlFor={id} className="text-sm font-medium text-[#717273] leading-5">
        {label}
      </label>
      <div className="relative w-full" title={managedHint}>
        <input
          id={id}
          {...registration}
          type={type}
          placeholder={placeholder}
          disabled
          readOnly
          aria-disabled="true"
          aria-describedby={`${id}-managed-hint`}
          className="bg-[#f3f4f6] border border-[#e2e8f0] rounded-lg h-10 px-4 py-2 text-sm text-[#717273] placeholder:text-[#a3a3a3] outline-none w-full cursor-not-allowed"
        />
        <span id={`${id}-managed-hint`} className="sr-only">
          {managedHint}
        </span>
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function PasswordField({
  label,
  id,
  registration,
  error,
  forgotPasswordLabel,
  showPasswordLabel,
  hidePasswordLabel,
}: {
  label: string;
  id: string;
  registration: React.InputHTMLAttributes<HTMLInputElement>;
  error?: string;
  forgotPasswordLabel: string;
  showPasswordLabel: string;
  hidePasswordLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-[4px] w-full">
      <div className="flex gap-1 items-baseline w-full">
        <label htmlFor={id} className="text-sm font-medium text-[#131315] leading-5 shrink-0">
          {label}
        </label>
        <Link
          to="/forgot-password"
          className="flex-1 text-right text-sm text-[#717273] hover:underline leading-5"
        >
          {forgotPasswordLabel}
        </Link>
      </div>
      <div className="bg-white border border-[#e2e8f0] rounded-lg h-10 flex items-center px-4 py-2 gap-2 w-full">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          {...(registration as React.InputHTMLAttributes<HTMLInputElement>)}
          className="flex-1 min-w-0 text-sm text-[#131315] placeholder:text-[#717273] outline-none bg-transparent"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="p-1 shrink-0 text-[#717273] hover:text-gray-900"
          aria-label={visible ? hidePasswordLabel : showPasswordLabel}
        >
          {visible ? <Eye width={20} height={20} /> : <EyeClosed width={20} height={20} />}
        </button>
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

export default function PersonalInfoTab() {
  const { t } = useTranslation(['settings', 'common']);
  const { data: profile, isLoading, isError, refetch } = useGetProfile();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const profileSchema = useMemo(
    () =>
      yup.object({
        full_name: yup
          .string()
          .required(t('settings:personal.validation.name_required'))
          .min(1, t('settings:personal.validation.name_required')),
        email: yup
          .string()
          .email(t('settings:personal.validation.email_invalid'))
          .required(t('settings:personal.validation.email_required')),
        telephone: yup.string().nullable().optional(),
        current_password: yup.string().optional(),
        new_password: yup.string().optional(),
        confirm_password: yup.string().optional(),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(profileSchema) as never,
    defaultValues: { full_name: '', email: '', telephone: '' },
  });

  useEffect(() => {
    if (profile) {
      reset({
        full_name: profile.full_name,
        email: profile.email,
        telephone: profile.telephone ?? '',
      });
    }
  }, [profile, reset]);

  const onSubmit = async (values: FormValues) => {
    setSuccessMsg(null);
    const isAppManaged = profile && !profile.has_password;
    try {
      await updateProfile.mutateAsync({
        full_name: isAppManaged ? profile!.full_name : values.full_name,
        email: isAppManaged ? profile!.email : values.email,
        telephone: values.telephone || null,
      });
    } catch (err: unknown) {
      const detail =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          ? (err as { response: { data: { detail: string } } }).response.data.detail
          : err instanceof Error
            ? err.message
            : t('settings:personal.errors.update_failed');
      setError('email', { message: detail });
      return;
    }

    const anyPassword =
      values.current_password || values.new_password || values.confirm_password;
    if (anyPassword) {
      if (!values.current_password || !values.new_password || !values.confirm_password) {
        setError('current_password', {
          message: t('settings:personal.validation.password_fields_required'),
        });
        return;
      }
      try {
        await changePassword.mutateAsync({
          current_password: values.current_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        });
      } catch (err: unknown) {
        const detail =
          err &&
          typeof err === 'object' &&
          'response' in err &&
          (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
            ? (err as { response: { data: { detail: string } } }).response.data.detail
            : err instanceof Error
              ? err.message
              : t('settings:personal.errors.password_failed');
        setError('current_password', { message: detail });
        return;
      }
    }

    setSuccessMsg(t('settings:personal.save_success'));
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500 py-4">{t('common:loading.generic')}</p>;
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <WarningCircle className="h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium text-gray-700">{t('settings:personal.load_error')}</p>
        <p className="text-xs text-gray-500">{t('settings:shared.load_error_hint')}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshDouble className="h-4 w-4" />
          {t('common:buttons.try_again')}
        </button>
      </div>
    );
  }

  const isAppManaged = Boolean(profile && !profile.has_password);
  const managedHint = t('settings:personal.innotaxes_managed_hint');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {successMsg ? (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          {successMsg}
        </div>
      ) : null}

      <div className="bg-[#f9fafb] border border-[#e2e8f0] rounded-xl pt-6 px-6 flex flex-col gap-6 max-w-lg">
        <div className="flex flex-col gap-4">
          {isAppManaged ? (
            <>
              <AppManagedField
                label={t('settings:personal.your_name')}
                id="full_name"
                registration={register('full_name')}
                error={errors.full_name?.message}
                placeholder={t('settings:personal.name_placeholder')}
                managedHint={managedHint}
              />
              <AppManagedField
                label={t('settings:personal.email')}
                id="email"
                registration={register('email')}
                error={errors.email?.message}
                type="email"
                placeholder={t('settings:personal.email_placeholder')}
                managedHint={managedHint}
              />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-[4px] w-full">
                <label htmlFor="full_name" className="text-sm font-medium text-[#131315] leading-5">
                  {t('settings:personal.your_name')}
                </label>
                <input
                  id="full_name"
                  {...register('full_name')}
                  type="text"
                  placeholder={t('settings:personal.name_placeholder')}
                  className="bg-white border border-[#e2e8f0] rounded-lg h-10 px-4 py-2 text-sm text-[#131315] placeholder:text-[#717273] outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
                {errors.full_name ? (
                  <p className="text-xs text-red-600">{errors.full_name.message}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-[4px] w-full">
                <label htmlFor="email" className="text-sm font-medium text-[#131315] leading-5">
                  {t('settings:personal.email')}
                </label>
                <input
                  id="email"
                  {...register('email')}
                  type="email"
                  placeholder={t('settings:personal.email_placeholder')}
                  className="bg-white border border-[#e2e8f0] rounded-lg h-10 px-4 py-2 text-sm text-[#131315] placeholder:text-[#717273] outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
                {errors.email ? (
                  <p className="text-xs text-red-600">{errors.email.message}</p>
                ) : null}
              </div>
            </>
          )}

          <div className="flex flex-col gap-[4px] w-full">
            <label htmlFor="telephone" className="text-sm font-medium text-[#131315] leading-5">
              {t('settings:personal.telephone')}
            </label>
            <input
              id="telephone"
              {...register('telephone')}
              type="tel"
              placeholder={t('settings:personal.telephone_placeholder')}
              className="bg-white border border-[#e2e8f0] rounded-lg h-10 px-4 py-2 text-sm text-[#131315] placeholder:text-[#717273] outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
            {errors.telephone ? (
              <p className="text-xs text-red-600">{errors.telephone.message}</p>
            ) : null}
          </div>
        </div>

        {profile?.has_password ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-[18px] font-normal leading-7 text-[#131315]">
              {t('settings:personal.change_password')}
            </h2>

            <PasswordField
              label={t('settings:personal.current_password')}
              id="current_password"
              registration={register('current_password')}
              error={errors.current_password?.message}
              forgotPasswordLabel={t('settings:personal.forgot_password')}
              showPasswordLabel={t('settings:personal.show_password')}
              hidePasswordLabel={t('settings:personal.hide_password')}
            />
            <PasswordField
              label={t('settings:personal.new_password')}
              id="new_password"
              registration={register('new_password')}
              error={errors.new_password?.message}
              forgotPasswordLabel={t('settings:personal.forgot_password')}
              showPasswordLabel={t('settings:personal.show_password')}
              hidePasswordLabel={t('settings:personal.hide_password')}
            />
            <PasswordField
              label={t('settings:personal.confirm_password')}
              id="confirm_password"
              registration={register('confirm_password')}
              error={errors.confirm_password?.message}
              forgotPasswordLabel={t('settings:personal.forgot_password')}
              showPasswordLabel={t('settings:personal.show_password')}
              hidePasswordLabel={t('settings:personal.hide_password')}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 rounded-xl border border-[#e2e8f0] bg-white px-5 py-4">
            <p className="text-sm font-semibold text-[#131315]">
              {t('settings:personal.innotaxes_signed_in_title')}
            </p>
            <p className="text-sm text-[#525353]">{t('settings:personal.innotaxes_signed_in_body')}</p>
          </div>
        )}

        <div className="flex justify-center pb-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg border border-[#6ECFCF] bg-[#6ECFCF] px-8 py-2 text-sm font-semibold text-[#131315] hover:brightness-95 disabled:opacity-60"
          >
            {isSubmitting ? t('common:buttons.saving') : t('common:buttons.save')}
          </button>
        </div>
      </div>
    </form>
  );
}
